import React, { useEffect, useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  CircularProgress,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import VisibilityIcon from '@mui/icons-material/Visibility';
import remitoVentaService, { RemitoVenta } from '../../services/remitoVentaService';
import PDFViewer from '../../components/shared/PDFViewer';
import api from '../../services/api';
import { getFileNameFromContentDisposition } from '../../utils/contentDisposition';
import { getApiErrorMessage } from '../../utils/apiError';

const Remitos: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const { enqueueSnackbar } = useSnackbar();

  const getFirstDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  };

  const getLastDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0);
  };

  const [remitos, setRemitos] = useState<RemitoVenta[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [fechaDesde, setFechaDesde] = useState<string>(() =>
    getFirstDayOfMonth().toISOString().split('T')[0]
  );
  const [fechaHasta, setFechaHasta] = useState<string>(() =>
    getLastDayOfMonth().toISOString().split('T')[0]
  );
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedRemito, setSelectedRemito] = useState<number | null>(null);
  const [pdfFileName, setPdfFileName] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState<number | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [remitoDetalle, setRemitoDetalle] = useState<RemitoVenta | null>(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);

  const fetchRemitos = async (
    pageNumber: number,
    pageSize: number,
    search: string,
    desde: string,
    hasta: string
  ) => {
    try {
      setLoading(true);
      const validPageSize = Math.max(1, Math.min(20, pageSize));
      const response = await remitoVentaService.getRemitosVenta({
        pageNumber: pageNumber,
        pageSize: validPageSize,
        searchTerm: search || undefined,
        fechaDesde: desde ? new Date(desde) : undefined,
        fechaHasta: hasta ? new Date(hasta) : undefined,
      });
      setRemitos(response.items);
      setTotalRecords(response.totalCount);
    } catch (error: unknown) {
      console.error('Error al cargar remitos:', error);
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar los remitos'), {
        variant: 'error',
        autoHideDuration: 5000,
      });
      setRemitos([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchRemitos(page + 1, rowsPerPage, searchTerm, fechaDesde, fechaHasta);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchTerm, fechaDesde, fechaHasta]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleImprimirPDF = async (nroFormulario: number) => {
    try {
      setPdfLoading(nroFormulario);
      const response = await api.get(`/api/RemitosVenta/${nroFormulario}/pdf`, {
        responseType: 'blob',
      });
      const cd = (response.headers as Record<string, string>)?.['content-disposition'];
      const backendFileName = getFileNameFromContentDisposition(cd);
      setPdfFileName(backendFileName ?? `Remito_${nroFormulario}.pdf`);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setSelectedRemito(nroFormulario);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al generar el PDF'), {
        variant: 'error',
        autoHideDuration: 5000,
      });
    } finally {
      setPdfLoading(null);
    }
  };

  const handleClosePDF = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl(null);
    setSelectedRemito(null);
    setPdfFileName(null);
  };

  const handleVerDetalle = async (nroFormulario: number) => {
    setLoadingDetalle(true);
    setViewDialogOpen(true);
    setRemitoDetalle(null);
    try {
      const detalle = await remitoVentaService.getRemitoVenta(nroFormulario);
      setRemitoDetalle(detalle);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar el remito'), {
        variant: 'error',
      });
      setViewDialogOpen(false);
    } finally {
      setLoadingDetalle(false);
    }
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setRemitoDetalle(null);
  };

  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (dateVal?: string | Date) => {
    if (!dateVal) return '-';
    try {
      const date = dateVal instanceof Date ? dateVal : new Date(dateVal);
      return date.toLocaleDateString('es-AR');
    } catch {
      return '-';
    }
  };

  const formatComprobante = (remito: RemitoVenta) => {
    const { letra, nroSuc, nroComprobante } = remito;
    if (!letra || nroSuc === undefined || nroComprobante === undefined || nroSuc === null || nroComprobante === null) {
      return remito.nroRemito ?? '-';
    }
    const formatPart = (value: number | string, length: number) => {
      if (value === null || value === undefined) return ''.padStart(length, '0');
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) return numeric.toString().padStart(length, '0');
      return String(value).padStart(length, '0');
    };
    const sucursal = formatPart(nroSuc, 5);
    const comprobante = formatPart(nroComprobante, 8);
    const letraNormalizada = letra.trim().toUpperCase();
    return letraNormalizada ? `${letraNormalizada}-${sucursal}-${comprobante}` : (remito.nroRemito ?? '-');
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              color: '#795548',
              fontSize: { xs: '1.5rem', sm: '2rem' },
              mb: 3,
            }}
          >
            Remitos de Venta
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Buscar por número, cliente, nombre..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Desde"
                  value={fechaDesde}
                  onChange={(e) => {
                    setFechaDesde(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Hasta"
                  value={fechaHasta}
                  onChange={(e) => {
                    setFechaHasta(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </Box>

          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 2,
              boxShadow: 'none',
              border: '1px solid #e0e0e0',
              overflowX: 'auto',
            }}
          >
            <Table
              size={isTablet ? 'small' : 'medium'}
              sx={{
                minWidth: 800,
                '& .MuiTableCell-root': {
                  fontSize: { xs: '0.75rem', md: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '10px 6px', md: '12px 8px' },
                },
              }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Nro. Remito</TableCell>
                  <TableCell sx={{ fontWeight: 600, display: { xs: 'none', md: 'table-cell' } }}>
                    Fecha
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, display: { xs: 'none', lg: 'table-cell' }, minWidth: 110 }}>
                    Nro. Comprobante
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, display: { xs: 'none', md: 'table-cell' } }}>
                    Cliente
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Importe Total
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, display: { xs: 'none', xl: 'table-cell' }, minWidth: 80 }}>
                    Usuario
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center" width={120}>
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {remitos.map((remito) => (
                  <TableRow
                    key={remito.nroFormulario}
                    sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>{remito.nroFormulario}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {formatDate(remito.fechaEmision)}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, whiteSpace: 'nowrap' }}>
                      {formatComprobante(remito)}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {remito.nroCliente ?? '-'}
                    </TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {remito.nombre?.trim() ?? '-'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1976d2' }} align="right">
                      {formatCurrency(remito.importeTotal)}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' } }}>
                      {remito.usuario ?? '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Visualizar">
                          <IconButton
                            onClick={() => handleVerDetalle(remito.nroFormulario)}
                            size="small"
                            sx={{
                              bgcolor: '#2196f3',
                              color: '#fff',
                              borderRadius: 1.5,
                              width: 32,
                              height: 32,
                              '&:hover': { bgcolor: '#1976d2', transform: 'scale(1.1)' },
                              transition: 'all 0.2s',
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Imprimir PDF">
                          <IconButton
                            onClick={() => handleImprimirPDF(remito.nroFormulario)}
                            size="small"
                            disabled={pdfLoading === remito.nroFormulario}
                            sx={{
                              bgcolor: '#2196f3',
                              color: '#fff',
                              borderRadius: 1.5,
                              width: 32,
                              height: 32,
                              '&:hover': { bgcolor: '#1976d2', transform: 'scale(1.1)' },
                              '&:disabled': { bgcolor: '#ccc' },
                              transition: 'all 0.2s',
                            }}
                          >
                            {pdfLoading === remito.nroFormulario ? (
                              <CircularProgress size={16} sx={{ color: '#fff' }} />
                            ) : (
                              <PrintIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {loading && remitos.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!loading && remitos.length === 0 && (
            <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
              No hay remitos para mostrar
            </Typography>
          )}

          <TablePagination
            rowsPerPageOptions={[5, 10, 20]}
            component="div"
            count={totalRecords}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas por página"
            sx={{
              borderTop: '1px solid #e0e0e0',
              mt: 1,
            }}
          />
        </CardContent>
      </Card>

      {pdfUrl && selectedRemito && (
        <PDFViewer
          pdfUrl={pdfUrl}
          onClose={handleClosePDF}
          title={`Remito ${selectedRemito}`}
          documentId={String(selectedRemito)}
          documentType="remito"
          downloadFileName={pdfFileName ?? undefined}
        />
      )}

      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle>Detalle del Remito</DialogTitle>
        <DialogContent>
          {loadingDetalle && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!loadingDetalle && remitoDetalle && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography>
                <strong>Nro. Formulario:</strong> {remitoDetalle.nroFormulario}
              </Typography>
              <Typography>
                <strong>Nro. Remito:</strong> {remitoDetalle.nroRemito ?? '-'}
              </Typography>
              <Typography>
                <strong>Fecha:</strong> {formatDate(remitoDetalle.fechaEmision)}
              </Typography>
              <Typography>
                <strong>Cliente:</strong> {remitoDetalle.nroCliente} - {remitoDetalle.nombre}
              </Typography>
              <Typography>
                <strong>Importe Total:</strong> {formatCurrency(remitoDetalle.importeTotal)}
              </Typography>
              {remitoDetalle.descripcion && (
                <Typography>
                  <strong>Descripción:</strong> {remitoDetalle.descripcion}
                </Typography>
              )}
              {remitoDetalle.detalles && remitoDetalle.detalles.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Items:
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Código</TableCell>
                        <TableCell>Descripción</TableCell>
                        <TableCell align="right">Cantidad</TableCell>
                        <TableCell align="right">P. Unit.</TableCell>
                        <TableCell align="right">Subtotal</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {remitoDetalle.detalles.map((d) => (
                        <TableRow key={d.nroItem}>
                          <TableCell>{d.codigoArticulo}</TableCell>
                          <TableCell>{d.descripcionArticulo}</TableCell>
                          <TableCell align="right">{d.cantidad}</TableCell>
                          <TableCell align="right">{formatCurrency(Number(d.precioUnitario))}</TableCell>
                          <TableCell align="right">{formatCurrency(Number(d.subtotal))}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Remitos;
